import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';
// Utils
import useAcl from '@/utils/hooks/user/useAcl';

// Types
import type { SubMenuEntryType } from '@/lib/platform/menu/menu';

// Components
import Icon from '@/components/base/Icon';
import Badge from '@/components/base/Badge';
import Shining from '@/components/effects/Shining';
import type { MenuEntriesProps } from '@/components/layouts/LayoutPlatform/components/Menu/components/MenuEntries';
import { formatEntryLinks } from '@/components/layouts/LayoutPlatform/components/Menu/components/MenuEntry';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
import { useNewFeatureNotifications } from '@/utils/hooks/user/useNewFeatureNotifications';

type SubEntriesProps = Pick<MenuEntriesProps, 'isMenuOpen'> & {
    subMenuItems: SubMenuEntryType[];
    className?: string;
};

export function SubEntries({ isMenuOpen, subMenuItems, className }: SubEntriesProps) {
    const { t } = useTranslation();
    const pathname = usePathname();
    const params = useParams();
    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    const { hasAcl } = useAcl();

    return (
        <div className={clsx('sub-menu flex cursor-default flex-col gap-3', className)}>
            {subMenuItems.map((subEntry) => {
                const { formattedUrlActiveMenu, formattedLink } = formatEntryLinks(
                    subEntry,
                    params
                );
                const isActive =
                    pathname === formattedLink || formattedUrlActiveMenu.includes(pathname);

                const requiresPlanAbove =
                    subEntry?.acl?.permissions_plan_restriction &&
                    !hasAcl(subEntry.acl.permissions_plan_restriction);

                const isNotClickable = requiresPlanAbove && subEntry.onClick === undefined;

                return (
                    <TooltipPlanRestriction key={subEntry.name} disabled={!isNotClickable}>
                        <div>
                            <Link
                                href={subEntry.link}
                                onClick={
                                    subEntry.onClick !== undefined
                                        ? (e) => subEntry.onClick(e, hasAcl)
                                        : undefined
                                }
                                className={clsx(
                                    'group relative flex flex-wrap w-full items-center gap-1 font-GMedium text-xs hover:text-icoBlue',
                                    {
                                        hidden: !isMenuOpen,
                                        'text-icoBlue': isActive,
                                        'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                            !isActive && !isNotClickable,
                                        'pointer-events-none cursor-not-allowed text-icoGray-500/40 dark:text-icoDarkMode-wolf/40':
                                            isNotClickable
                                    }
                                )}
                                data-testid={`menu-subitem-${subEntry.id}`}>
                                {t(subEntry.name)}
                                {requiresPlanAbove ? (
                                    <Icon icon="crown" className="text-icoBlue" />
                                ) : null}

                                {isNewFeatureOnPages([subEntry?.link, ...(subEntry.urlActiveMenu ?? [])]) ? (
                                    <span className="flex-1 pt-px">
                                        <Shining className="rounded-2xl">
                                            <Badge variant="new" />
                                        </Shining>
                                    </span>
                                ) : null }
                            </Link>
                        </div>
                    </TooltipPlanRestriction>
                );
            })}
        </div>
    );
}
